import React, { useState, useEffect } from 'react';
import {
    ModalActionsComponent,
    PasslySelect,
    PasslyMaskedInput,
    PasslyButton,
    PasslyCheckbox,
    PasslyTextField
} from '@app/components';
import { useTypedTranslation } from '@app/hooks';
import { IPolicyAuth, IRadiusClient, RadiusClientFeature } from '@app/models';
import { FormMode } from '@app/helpers/enums';
import { FormControl } from '@material-ui/core';
import { singleIPRegex, onPressEnterModal } from '@app/helpers';

interface IProps {
    authPolicies: IPolicyAuth[];
    toggleModal: Function;
    formMode: FormMode;
    onAddClient: Function;
    clients: IRadiusClient[];
    index?: number;
    existingConfig?: IRadiusClient;
}

export const AddRadiusClient: React.SFC<IProps> = ({ toggleModal, authPolicies, formMode, onAddClient, clients, index, existingConfig }) => {
    const { translation } = useTypedTranslation();

    const [friendlyName, setFriendlyName] = useState(existingConfig?.name || '');
    const [clientIP, setClientIP] = useState(existingConfig?.ip || '');
    const [sharedSecret, setSharedSecret] = useState(existingConfig?.password || '');
    const [clientFeatures, setClientFeatures] = useState(existingConfig?.features || RadiusClientFeature.None); 
    const [confirmSharedSecret, setConfirmSharedSecret] = useState('');
    const [selectedAuthPolicy, setSelectedAuthPolicy] = useState(formMode === FormMode.Add ? authPolicies[0].Id : existingConfig!.policy.value);
    const [selectedAuthPolicyLabel, setSelectedAuthPolicyLabel] = useState(formMode === FormMode.Add ? authPolicies[0].DisplayName : existingConfig!.policy.label);
    const [isValid, setIsValid] = useState(false);
    const [sharedSecretError, setSharedSecretError] = useState(false);
    const [client, setClient] = useState({} as IRadiusClient);
    const [addMore, setAddMore] = useState(false);
    const [inUseIPs, setInUseIPs] = useState<string[]>(clients ? clients.map(client => client.ip) : []);
    const [inUseNames, setInUseNames] = useState<string[]>(clients ? clients.map(client => client.name) : []);
    const [ipInUseError, setIpInUseError] = useState(false);
    const [nameInUseError, setNameInUseError] = useState(false);
    const [showSecret, setShowSecret] = useState(false);

    useEffect(() => {
        if (confirmSharedSecret !== '' && confirmSharedSecret !== sharedSecret) {
            setSharedSecretError(true);
        }
        else {
            setSharedSecretError(false);
        }
    }, [sharedSecret, confirmSharedSecret]);

    // form validation
    useEffect(() => {
        if (((sharedSecret !== '' && confirmSharedSecret !== '') || (!showSecret && formMode === FormMode.Edit)) &&
            friendlyName !== '' && clientIP !== '' && selectedAuthPolicy !== '' && !sharedSecretError && !ipInUseError && !nameInUseError) {
            let client: IRadiusClient = {
                name: friendlyName,
                ip: clientIP,
                password: sharedSecret,
                policy: { label: selectedAuthPolicyLabel, value: selectedAuthPolicy },
                features: clientFeatures
            };

            setClient(client);
            setIsValid(true);
        }
        else {
            setIsValid(false);
        }
    }, [friendlyName, clientIP, sharedSecret, confirmSharedSecret, selectedAuthPolicy, clientFeatures,
        sharedSecretError, ipInUseError, nameInUseError]);

    useEffect(() => {
        setInUseIPs(clients.map(client => client.ip));
        setInUseNames(clients.map(client => client.name));
    }, [clients]);

    const handleAddClient = () => {
        if (formMode === FormMode.Add) {
            onAddClient(client);
        }
        else {
            onAddClient(client, index);
        }

        if (addMore) {
            // reset form
            setFriendlyName('');
            setClientIP('');
            setSharedSecret('');
            setConfirmSharedSecret('');
            setIsValid(false);
            setClient({} as IRadiusClient);
            setClientFeatures(RadiusClientFeature.None);
        }
        else {
            toggleModal(false);
        }
    };

    const handleSetFriendlyName = (e: React.ChangeEvent<HTMLInputElement>) => {
        setFriendlyName(e.target.value);
        if (inUseNames.includes(e.target.value) && existingConfig?.name !== e.target.value) {
            setNameInUseError(true);
        }
        else {
            setNameInUseError(false);
        }
    };

    const handleSetClientIp = (e: React.ChangeEvent<HTMLInputElement>) => {
        setClientIP(e.target.value);
        if (inUseIPs.includes(e.target.value) && existingConfig?.ip !== e.target.value) {
            setIpInUseError(true);
        }
        else {
            setIpInUseError(false);
        }
    };

    const handleSharedSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSharedSecret(e.target.value);
    };

    const handleConfirmSecretChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setConfirmSharedSecret(e.target.value);
    };

    const handleAuthPolicyChange = (e: any) => {
        setSelectedAuthPolicy(e.target.value);
        let authPolicy: IPolicyAuth[] = authPolicies.filter((policy) => { return policy.Id === e.target.value; });
        setSelectedAuthPolicyLabel(authPolicy[0].DisplayName);
    };

    const handleShowSecret = () => {
        setSharedSecret('');
        setShowSecret(true);
    };

    const setIsAddMore = (checked: boolean) => setAddMore(checked);
    const setRequireMessageAuthenticator = (checked: boolean) => {
        if (checked) setClientFeatures(clientFeatures | RadiusClientFeature.RequireMessageAuthenticator);
        else setClientFeatures(clientFeatures & (~RadiusClientFeature.RequireMessageAuthenticator));
    }

    return (
        <>
            <form className='modal__form' method='post' tabIndex={0} onKeyPress={onPressEnterModal(handleAddClient, !isValid)}>
                <FormControl>
                    <PasslyTextField
                        name='friendlyName'
                        label={'RADIUS_CLIENT_NAME_LABEL'}
                        value={friendlyName}
                        onChange={handleSetFriendlyName}
                        id='friendlyName-input'
                        inputProps={{ maxLength: 100 }}
                        autoFocus
                        required
                        error={nameInUseError}
                        helperText={nameInUseError && translation('RADIUS_CLIENT_NAME_DUPLICATE')}
                        fullWidth
                    />
                </FormControl>

                <FormControl>
                    <PasslyTextField
                        id='radiusIP'
                        label={'RADIUS_CLIENT_IP_LABEL'}
                        value={clientIP}
                        onChange={handleSetClientIp}
                        fullWidth
                        InputProps={{
                            inputComponent: PasslyMaskedInput,
                            inputProps: {
                                maskSize: 18,
                                maskRegex: singleIPRegex
                            },
                        }}
                        required
                        error={ipInUseError}
                        helperText={ipInUseError ? translation('RADIUS_CLIENT_IP_DUPLICATE') : undefined}
                    />
                </FormControl>

                {(formMode === FormMode.Add || showSecret) &&
                    <>
                        <FormControl>
                            <PasslyTextField
                                id='sharedSecret-input'
                                name='sharedSecret'
                                type='password'
                                label={'RADIUS_CLIENT_PASSWORD_LABEL'}
                                value={sharedSecret}
                                onChange={handleSharedSecretChange}
                                fullWidth
                                required
                            />
                        </FormControl>


                        <FormControl error={sharedSecretError}>
                            <PasslyTextField
                                id='confirmSharedSecret-input'
                                name='confirmSharedSecret'
                                type='password'
                                label={'RADIUS_CLIENT_PASSWORD_CONFIRM_LABEL'}
                                value={confirmSharedSecret}
                                onChange={handleConfirmSecretChange}
                                fullWidth
                                error={sharedSecretError}
                                helperText={sharedSecretError ? translation('CLIENT_ERROR_MISMATCH_PASSWORD') : undefined}
                            />
                        </FormControl>
                    </>
                }

                <FormControl>
                    <PasslySelect
                        name='PolicyId'
                        value={selectedAuthPolicy}
                        onChange={handleAuthPolicyChange}
                        id='authPolicyId-input'
                        label={'APP_AUTH_POLICY'}
                        options={authPolicies}
                        optionLabel='DisplayName'
                        optionValue='Id'
                    />
                </FormControl>

                <FormControl>
                    <label className='kaseya-ui-form-label'>
                        <span className='form-label__value'>Features</span>
                    </label>
                    <PasslyCheckbox
                        name='clientFeatures'
                        checked={(clientFeatures & RadiusClientFeature.RequireMessageAuthenticator) == RadiusClientFeature.RequireMessageAuthenticator}
                        onChange={setRequireMessageAuthenticator}
                        label={translation('RADIUS_REQUIRE_MESSAGE_AUTHENTICATOR')}
                    />
                </FormControl>

                {!showSecret && formMode === FormMode.Edit &&
                    <PasslyButton variant='text' color='primary' onClick={() => handleShowSecret()}>{translation('SET_CLIENT_SECRET')}</PasslyButton>
                }

                <PasslyCheckbox
                    className='modal__form--padding-left'
                    name='provisionRequire2FA'
                    checked={addMore}
                    onChange={setIsAddMore}
                    label={translation('ADD_ANOTHER')}
                />
            </form>
            <ModalActionsComponent
                primaryBtn={formMode === FormMode.Add ? translation('ADD_RADIUS_CLIENT') : translation('UPDATE_BUTTON')}
                secondaryBtn={translation('CANCEL')}
                onClickPrimary={handleAddClient}
                onClickSecondary={toggleModal}
                disabled={!isValid}
                id='addRadiusClient'
            />
    </>
    );
};
